import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, History, MoveRight, Printer, Unlock, SlidersHorizontal, X } from "lucide-react";
import { fmtQty, GRADES, type StockUnitRecord } from "@/types/inventory";
import { StockStatusBadge, Swatch } from "./StockStatusBadge";

const STATUS_FILTERS = [
  { key: "ON_HAND", label: "In stock", status: "" },
  { key: "GONE", label: "Gone", status: "DISPATCHED,ISSUED,SCRAPPED,CANCELLED" },
  { key: "ALL", label: "All", status: "IN_STOCK,RESERVED,DISPATCHED,ISSUED,SCRAPPED,CANCELLED" },
];

interface Props {
  warehouseId: string;
  search: string;
  productId: string;
  variantId: string;
  reloadKey: number;
  onClearProduct: () => void;
  onHistory: (id: string) => void;
  onTransfer: (units: StockUnitRecord[]) => void;
  onAdjust: (units: StockUnitRecord[]) => void;
  onInspect: (unit: StockUnitRecord) => void;
}

/** Roll-wise stock, with ticks to move or adjust several at once */
export function RollsTab({ warehouseId, search, productId, variantId, reloadKey, onClearProduct, onHistory, onTransfer, onAdjust, onInspect }: Props) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canTransfer = hasPermission("inventory.transfer");
  const canAdjust = hasPermission("inventory.adjust");
  const [urlParams] = useSearchParams();
  // A roll opened from the global search is listed whatever its status
  const [filter, setFilter] = useState(urlParams.get("filter") === "all" ? "ALL" : "ON_HAND");
  const [picked, setPicked] = useState<Map<string, StockUnitRecord>>(new Map());

  const list = usePagedList<StockUnitRecord>({
    deps: [warehouseId, search, productId, variantId, filter, reloadKey],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (warehouseId) qs.set("warehouse_id", warehouseId);
      if (productId) qs.set("product_id", productId);
      if (variantId) qs.set("variant_id", variantId);
      if (search.trim()) qs.set("search", search.trim());
      const status = STATUS_FILTERS.find((f) => f.key === filter)?.status;
      if (status) qs.set("status", status);
      try {
        return unwrapPage<StockUnitRecord>(await api.get(`/api/Inventory/Units?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load rolls");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  // After stock changes, ticks on rolls that are gone or moved would be stale
  useEffect(() => setPicked(new Map()), [reloadKey, filter]);

  const toggle = (u: StockUnitRecord) =>
    setPicked((m) => {
      const next = new Map(m);
      if (next.has(u.id)) next.delete(u.id);
      else next.set(u.id, u);
      return next;
    });
  const selectable = list.rows.filter((u) => u.unit_status === "IN_STOCK" || (u.unit_status === "RESERVED" && canAdjust));
  const allOnPage = selectable.length > 0 && selectable.every((u) => picked.has(u.id));
  const togglePage = () =>
    setPicked((m) => {
      const next = new Map(m);
      for (const u of selectable) allOnPage ? next.delete(u.id) : next.set(u.id, u);
      return next;
    });
  const chosen = [...picked.values()];
  const anyHeld = chosen.some((u) => u.unit_status === "RESERVED");
  const allHeld = chosen.length > 0 && chosen.every((u) => u.unit_status === "RESERVED");

  const release = async () => {
    try {
      await api.post("/api/Inventory/Release", { unit_ids: chosen.map((u) => u.id) });
      success("Released", `${chosen.length} roll${chosen.length === 1 ? "" : "s"} freed from their orders`);
      setPicked(new Map());
      list.reload();
    } catch (err: any) {
      error("Could not release", err.message || "Please try again");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
          >
            {f.label}
          </button>
        ))}
        {productId && (
          <button type="button" onClick={onClearProduct} className="h-8 px-3 rounded-full text-xs font-semibold border border-primary/40 bg-primary/10 text-primary inline-flex items-center gap-1.5">
            One product only <X className="size-3" />
          </button>
        )}
        {chosen.length > 0 && (
          <div className="ml-auto flex items-center gap-2 rounded-lg bg-muted px-3 py-1">
            <span className="text-xs font-semibold">{chosen.length} selected</span>
            {canTransfer && !anyHeld && (
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => onTransfer(chosen)}>
                <MoveRight className="size-3.5" /> Move
              </Button>
            )}
            {canAdjust && !anyHeld && (
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => onAdjust(chosen)}>
                <SlidersHorizontal className="size-3.5" /> Adjust
              </Button>
            )}
            {canAdjust && allHeld && (
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={release}>
                <Unlock className="size-3.5" /> Release from orders
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => navigate(`/inventory/labels?ids=${chosen.map((u) => u.id).join(",")}`)}>
              <Printer className="size-3.5" /> Print labels
            </Button>
            {canAdjust && chosen.length === 1 && (
              <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => onInspect(chosen[0]!)}>
                <ClipboardCheck className="size-3.5" /> Inspect
              </Button>
            )}
            {anyHeld && !allHeld && <span className="text-[11px] text-muted-foreground">Held rolls can only be released</span>}
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setPicked(new Map())} title="Clear selection">
              <X className="size-3.5" />
            </button>
          </div>
        )}
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {(canTransfer || canAdjust) && <input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label="Select all on this page" />}
                </TableHead>
                <TableHead>Roll</TableHead>
                <TableHead>Product / shade</TableHead>
                <TableHead>Lot</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead>Grade</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((u) => (
                <TableRow key={u.id} className={picked.has(u.id) ? "bg-primary/5" : ""}>
                  <TableCell>
                    {(u.unit_status === "IN_STOCK" ? canTransfer || canAdjust : u.unit_status === "RESERVED" && canAdjust) && <input type="checkbox" checked={picked.has(u.id)} onChange={() => toggle(u)} aria-label={`Select roll ${u.unit_no}`} />}
                  </TableCell>
                  <TableCell className="font-mono text-sm font-semibold">{u.unit_no}{u.barcode && <div className="text-[10px] font-normal text-muted-foreground">{u.barcode}</div>}</TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{u.product?.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      <Swatch hex={u.variant?.color_hex} />
                      {u.variant?.name || "No shade"}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{u.lot?.lot_number}</TableCell>
                  <TableCell className="text-sm">
                    {u.warehouse?.name}
                    {u.location && <div className="text-[11px] text-muted-foreground">{u.location}</div>}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    <div className="font-semibold">{fmtQty(u.quantity, u.unit)}</div>
                    {Number(u.quantity) !== Number(u.initial_quantity) && <div className="text-[11px] text-muted-foreground">of {fmtQty(u.initial_quantity)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{GRADES[u.grade] || u.grade}</TableCell>
                  <TableCell>
                    <StockStatusBadge status={u.unit_status} />
                    {u.unit_status === "RESERVED" && u.reserved_order_id && (
                      <div className="text-[11px] mt-0.5">
                        <Link to={`/orders/${u.reserved_order_id}`} className="text-primary hover:underline">
                          {u.reserved_order_number || "Order"}
                        </Link>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="size-8" title="History" onClick={() => onHistory(u.id)}>
                      <History className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-muted-foreground text-sm">
                    No rolls here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
    </div>
  );
}
